import {
  CryptoProError,
  type CadesProfile,
  type CryptoProAdapter,
  type SigningCertificate,
} from "./types";

// Константы CAdESCOM/CAPICOM из документации КриптоПро.
const CAPICOM_CURRENT_USER_STORE = 2;
const CAPICOM_MY_STORE = "My";
const CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED = 2;
const CAPICOM_CERTIFICATE_FIND_SHA1_HASH = 0;
const CADESCOM_BASE64_TO_BINARY = 1;
const CADES_TYPES: Readonly<Record<CadesProfile, number>> = {
  "CAdES-BES": 1,
  "CAdES-T": 5,
  "CAdES-X Long Type 1": 0x5d,
};

/** Объект плагина: свойства и методы приходят асинхронно. */
interface PluginObject {
  [member: string]: unknown;
}

interface CadesPlugin {
  CreateObjectAsync(name: string): Promise<PluginObject>;
  /** Версия скрипта cadesplugin_api.js, если он её сообщает. */
  JSModuleVersion?: string;
}

declare global {
  interface Window {
    cadesplugin?: CadesPlugin & PromiseLike<unknown>;
  }
}

export interface CadesPluginOptions {
  /**
   * Адрес cadesplugin_api.js. Скрипт подключается только по явной настройке
   * окружения: чужой код в окне ERP не грузится по умолчанию.
   */
  readonly pluginUrl?: string;
  /** Служба штампов времени: без неё доступен только CAdES-BES. */
  readonly timestampUrl?: string;
  /** Сколько ждать ответа плагина, мс. */
  readonly loadTimeoutMs?: number;
}

function unavailable(message: string, cause?: unknown): CryptoProError {
  return new CryptoProError("plugin_unavailable", message, { cause });
}

function loadScript(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = url;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("script load failed"));
    document.head.append(script);
  });
}

function withTimeout<T>(promise: PromiseLike<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error("timeout")), ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        window.clearTimeout(timer);
        reject(error instanceof Error ? error : new Error(String(error)));
      },
    );
  });
}

function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  // Кусками: один String.fromCharCode на большой файл переполняет стек.
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }

  return btoa(binary);
}

function asDate(value: unknown): Date {
  return value instanceof Date ? value : new Date(String(value));
}

/** Отказ пользователя в окне выбора ключа или ввода PIN. */
function isCancellation(error: unknown): boolean {
  const text = error instanceof Error ? error.message : String(error);

  return /0x8010006E|0x000004C7|0x800704C7|cancel|отмен/i.test(text);
}

async function call<T>(
  target: PluginObject,
  member: string,
  ...args: unknown[]
): Promise<T> {
  const method = target[member];
  if (typeof method !== "function") {
    throw new Error(`Метод ${member} недоступен`);
  }

  return (await (method as (...values: unknown[]) => unknown).apply(
    target,
    args,
  )) as T;
}

async function withStore<T>(
  plugin: CadesPlugin,
  work: (store: PluginObject) => Promise<T>,
): Promise<T> {
  const store = await plugin.CreateObjectAsync("CAdESCOM.Store");
  await call(
    store,
    "Open",
    CAPICOM_CURRENT_USER_STORE,
    CAPICOM_MY_STORE,
    CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED,
  );
  try {
    return await work(store);
  } finally {
    await call(store, "Close").catch(() => undefined);
  }
}

/**
 * Реальный адаптер к браузерному плагину КриптоПро (CAdESCOM).
 *
 * Подпись выполняется внутри плагина: сюда приходят только содержимое и
 * отпечаток сертификата, а возвращается готовая отделённая подпись. Закрытый
 * ключ никогда не читается и не сериализуется.
 */
export function createCadesPluginAdapter(
  options: CadesPluginOptions = {},
): CryptoProAdapter {
  const timeout = options.loadTimeoutMs ?? 15_000;
  const supportedProfiles: readonly CadesProfile[] = options.timestampUrl
    ? ["CAdES-BES", "CAdES-T", "CAdES-X Long Type 1"]
    : ["CAdES-BES"];
  // Плагин — «thenable»: вернуть его из async-функции напрямую значило бы
  // дождаться его готовности и потерять сам объект, поэтому он лежит в обёртке.
  let loaded: Promise<{ readonly api: CadesPlugin }> | null = null;

  async function load(): Promise<{ readonly api: CadesPlugin }> {
    try {
      if (window.cadesplugin === undefined && options.pluginUrl) {
        await withTimeout(loadScript(options.pluginUrl), timeout);
      }
      const plugin = window.cadesplugin;
      if (plugin === undefined) {
        throw unavailable("Плагин КриптоПро не найден в браузере.");
      }
      // В асинхронном режиме сам объект — обещание готовности плагина.
      await withTimeout(plugin, timeout);

      return { api: plugin };
    } catch (error) {
      if (error instanceof CryptoProError) throw error;
      throw unavailable("Плагин КриптоПро не отвечает.", error);
    }
  }

  const plugin = (): Promise<{ readonly api: CadesPlugin }> => {
    // Неудачную загрузку не запоминаем: после установки плагина хватит
    // повторить попытку без перезагрузки страницы.
    loaded ??= load().catch((error: unknown) => {
      loaded = null;
      throw error;
    });

    return loaded;
  };

  return {
    supportedProfiles,

    async ensureAvailable() {
      const { api } = await plugin();

      return { version: api.JSModuleVersion ?? null };
    },

    async listCertificates() {
      const { api } = await plugin();
      try {
        return await withStore(api, async (store) => {
          const list = (await Promise.resolve(
            store.Certificates,
          )) as PluginObject;
          const count = Number(await Promise.resolve(list.Count));
          const now = Date.now();
          const result: SigningCertificate[] = [];

          for (let index = 1; index <= count; index += 1) {
            const certificate = await call<PluginObject>(list, "Item", index);
            const hasKey = await call<boolean>(certificate, "HasPrivateKey");
            const validFrom = asDate(
              await Promise.resolve(certificate.ValidFromDate),
            );
            const validTo = asDate(
              await Promise.resolve(certificate.ValidToDate),
            );
            if (
              !hasKey ||
              validFrom.getTime() > now ||
              validTo.getTime() <= now
            ) {
              continue;
            }
            result.push({
              id: String(await Promise.resolve(certificate.Thumbprint)),
              subject: String(await Promise.resolve(certificate.SubjectName)),
              issuer: String(await Promise.resolve(certificate.IssuerName)),
              validFrom: validFrom.toISOString(),
              validTo: validTo.toISOString(),
            });
          }

          return result;
        });
      } catch (error) {
        if (error instanceof CryptoProError) throw error;
        throw unavailable(
          "Не удалось прочитать хранилище сертификатов.",
          error,
        );
      }
    },

    async signDetached({ certificateId, content, profile }) {
      if (!supportedProfiles.includes(profile)) {
        throw new CryptoProError(
          "profile_unsupported",
          `Профиль ${profile} не поддерживается в этом браузере.`,
        );
      }
      const { api } = await plugin();
      try {
        return await withStore(api, async (store) => {
          const all = (await Promise.resolve(
            store.Certificates,
          )) as PluginObject;
          const found = await call<PluginObject>(
            all,
            "Find",
            CAPICOM_CERTIFICATE_FIND_SHA1_HASH,
            certificateId,
          );
          if (Number(await Promise.resolve(found.Count)) === 0) {
            throw new CryptoProError(
              "certificate_not_found",
              "Сертификат больше не найден в хранилище.",
            );
          }
          const certificate = await call<PluginObject>(found, "Item", 1);

          const signer = await api.CreateObjectAsync("CAdESCOM.CPSigner");
          await call(signer, "propset_Certificate", certificate);
          await call(signer, "propset_CheckCertificate", true);
          if (profile !== "CAdES-BES" && options.timestampUrl) {
            await call(signer, "propset_TSAAddress", options.timestampUrl);
          }

          const data = await api.CreateObjectAsync("CAdESCOM.CadesSignedData");
          await call(
            data,
            "propset_ContentEncoding",
            CADESCOM_BASE64_TO_BINARY,
          );
          await call(data, "propset_Content", toBase64(content));
          const signature = await call<string>(
            data,
            "SignCades",
            signer,
            CADES_TYPES[profile],
            true,
          );

          return signature.replace(/\s+/g, "");
        });
      } catch (error) {
        if (error instanceof CryptoProError) throw error;
        if (isCancellation(error)) {
          throw new CryptoProError(
            "user_cancelled",
            "Подписание отменено пользователем.",
            { cause: error },
          );
        }
        throw new CryptoProError(
          "signing_failed",
          "Плагин КриптоПро не смог создать подпись.",
          { cause: error },
        );
      }
    },
  };
}

/** Адаптер для окружения без плагина: любая операция сообщает о его отсутствии. */
export function createUnavailableCryptoProAdapter(): CryptoProAdapter {
  const reject = <T>(): Promise<T> =>
    Promise.reject(unavailable("Плагин КриптоПро не настроен."));

  return {
    supportedProfiles: [],
    ensureAvailable: reject,
    listCertificates: reject,
    signDetached: reject,
  };
}
